import { Component } from '@theme/component';

/**
 * @typedef {Object} MapStore
 * @property {string} name
 * @property {string} street
 * @property {string} postal_code
 * @property {string} city
 * @property {string} country
 * @property {string} url
 * @property {string} email
 * @property {string} phone
 * @property {number} lat - Parsed latitude (valid range guaranteed by store-locator.js)
 * @property {number} lng - Parsed longitude (valid range guaranteed by store-locator.js)
 * @property {boolean} hasValidCoords - Always true for stores in mapStores
 */

/**
 * Interactive Google Map web component for the store locator feature.
 *
 * Responsibilities:
 * 1. Listen for the `stores:ready` CustomEvent from the parent `<store-locator>` element.
 * 2. Initialize a Google Map using the Maps JS API bootstrap loader (already in the DOM).
 * 3. Place all valid-coordinate stores as `AdvancedMarkerElement` pins.
 * 4. Cluster pins via the `@googlemaps/markerclusterer` CDN global.
 * 5. Auto-fit the map viewport to the bounding box of all stores — no hardcoded center/zoom.
 * 6. Always render the map in light mode (MAPB-01) — the store locator UI is hardcoded light (Phase 7), so the map must match.
 * 7. Open a single InfoWindow on marker click showing store name, address, phone, email, and website link.
 *    InfoWindow colors are derived from the active Tinker color scheme via resolved CSS variables.
 *
 * Dependencies (loaded synchronously in sections/store-locator.liquid before this module):
 * - Google Maps bootstrap loader (makes `google.maps.importLibrary` callable)
 * - @googlemaps/markerclusterer CDN script (exposes `markerClusterer` global)
 *
 * @extends {Component}
 */
class StoreMap extends Component {
  /**
   * The Google Maps `Map` instance.
   * @type {google.maps.Map | null}
   */
  #map = null;

  /**
   * The single reused `InfoWindow` instance.
   * One instance is created and reused across all markers — creating per-marker instances
   * causes DOM leaks and allows multiple windows open simultaneously (RESEARCH Pattern 3).
   * @type {google.maps.InfoWindow | null}
   */
  #infoWindow = null;

  /**
   * The MarkerClusterer instance managing pin clustering.
   * @type {import('@googlemaps/markerclusterer').MarkerClusterer | null}
   */
  #clusterer = null;

  /**
   * Map from ValidatedStore to its corresponding `AdvancedMarkerElement`.
   * Used for O(1) marker lookup during filter operations — avoids scanning the full
   * markers array and enables identity-based lookup (store object reference as key).
   * @type {Map<object, google.maps.marker.AdvancedMarkerElement>}
   */
  #markerMap = new Map();

  /**
   * Stores a filter event that arrived before the map finished initializing.
   * Applied at the end of `#initMap` so the default country zoom is not lost.
   * @type {object[] | null}
   */
  #pendingFilter = null;

  /**
   * Active requestAnimationFrame ID for camera animation.
   * Stored so concurrent animations can be cancelled.
   * @type {number | null}
   */
  #animationFrameId = null;

  /**
   * Called when the element is connected to the document's DOM.
   *
   * Registers a one-time listener for `stores:ready` on the parent `<store-locator>` element.
   * The listener fires `#initMap` asynchronously when store data is available.
   *
   * Synchronous per the Web Components spec — async work is triggered via event.
   */
  connectedCallback() {
    super.connectedCallback();

    const storeLocator = this.closest('store-locator');
    if (!storeLocator) {
      console.error('[Store Map] No parent <store-locator> element found.');
      return;
    }

    storeLocator.addEventListener(
      'stores:ready',
      (event) => {
        this.#initMap(/** @type {CustomEvent} */ (event).detail.mapStores);
      },
      { once: true }
    );

    // stores:filter fires repeatedly (not once) — each filter change dispatches a new event.
    storeLocator.addEventListener('stores:filter', (event) => {
      this.#handleFilter(/** @type {CustomEvent} */ (event).detail.mapStores);
    });

    // map:pan-to is dispatched by store-search.js to pan/zoom the map to a searched location.
    storeLocator.addEventListener('map:pan-to', (event) => {
      this.#handlePanTo(/** @type {CustomEvent} */ (event).detail);
    });

    // Handle incoming store:selected — pan map to the store and open its info popup (INTX-01).
    // Also fires for self-dispatched events (gmp-click path): panTo to current position is a
    // no-op and reopening the same InfoWindow is visually imperceptible. No guard needed.
    storeLocator.addEventListener('store:selected', (event) => {
      this.#handleStoreSelected(/** @type {CustomEvent} */ (event).detail.store);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#animationFrameId) {
      cancelAnimationFrame(this.#animationFrameId);
      this.#animationFrameId = null;
    }
  }

  /**
   * Initializes the Google Map, creates markers for all valid-coordinate stores,
   * clusters them, and fits the viewport to the store bounding box.
   *
   * @param {MapStore[]} mapStores - Stores with valid lat/lng coordinates.
   * @returns {Promise<void>}
   */
  async #initMap(mapStores) {
    try {
      const { Map, InfoWindow } = await google.maps.importLibrary('maps');
      const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');
      const { ColorScheme } = await google.maps.importLibrary('core');

      const mapId = this.dataset.mapId;
      const colorScheme = this.#detectColorScheme(ColorScheme);

      const mapCanvas = this.querySelector('[ref="mapCanvas"]');
      if (!mapCanvas) {
        throw new Error('Missing [ref="mapCanvas"] element inside <store-map>.');
      }

      // Create the map — colorScheme is init-only, cannot be changed after construction.
      // No maxZoom cap — users can freely zoom in. Cluster resolution is controlled separately
      // by SuperClusterAlgorithm maxZoom (currently 10).
      this.#map = new Map(mapCanvas, {
        mapId,
        colorScheme,
        disableDefaultUI: false,
      });

      // Single InfoWindow instance, reused across all markers (RESEARCH Pattern 3).
      // One instance ensures only one popup is open at a time.
      // headerDisabled: true removes the close button header row (~30px) for a compact popup
      // with no excess top whitespace. The window still closes on outside clicks or close() calls.
      this.#infoWindow = new InfoWindow({ headerDisabled: true });

      // Create AdvancedMarkerElement for each store with valid coordinates.
      // gmpClickable: true is REQUIRED — without it, click events silently fail (RESEARCH Pitfall 1).
      const bounds = new google.maps.LatLngBounds();

      for (const store of mapStores) {
        const position = { lat: store.lat, lng: store.lng };

        const marker = new AdvancedMarkerElement({
          position,
          map: this.#map,
          title: store.name,
          gmpClickable: true,
        });

        marker.addEventListener('gmp-click', () => {
          this.#openInfoWindow(marker, store);
          // Dispatch AFTER openInfoWindow so the popup opens immediately on the clicked pin.
          // store:selected propagates to <store-list> for scroll+highlight (INTX-02).
          this.dispatchEvent(new CustomEvent('store:selected', { bubbles: true, detail: { store } }));
        });

        this.#markerMap.set(store, marker);
        bounds.extend(position);
      }

      // Determine which markers to show initially — if a filter arrived before init
      // completed, use only the filtered subset to avoid a double fitBounds (which
      // causes a blank map render on initial load).
      let initialMarkers;
      let initialBounds;

      if (this.#pendingFilter) {
        const filteredMarkers = this.#pendingFilter
          .map((store) => this.#markerMap.get(store))
          .filter(Boolean);
        initialMarkers = filteredMarkers;

        // Hide markers not in the filtered set — they were added to the map
        // via the AdvancedMarkerElement constructor above and must be removed
        // manually since the clusterer only manages the filtered subset.
        // Safe to use marker.map = null here (unlike #handleFilter) because
        // these markers are never added to the clusterer (avoids bug #961).
        const filteredSet = new Set(filteredMarkers);
        for (const marker of this.#markerMap.values()) {
          if (!filteredSet.has(marker)) {
            marker.map = null;
          }
        }

        initialBounds = new google.maps.LatLngBounds();
        this.#pendingFilter.forEach((s) => initialBounds.extend({ lat: s.lat, lng: s.lng }));
        this.#pendingFilter = null;
      } else {
        initialMarkers = [...this.#markerMap.values()];
        initialBounds = bounds;
      }

      // Auto-fit the viewport to the bounding box of visible stores.
      if (initialMarkers.length > 0) {
        this.#map.fitBounds(initialBounds, 50);
      }

      // Cluster markers using the @googlemaps/markerclusterer CDN global.
      // SuperClusterAlgorithm maxZoom: 10 resolves clusters into individual pins at zoom 10.
      // SuperClusterAlgorithm does NOT inherit Map maxZoom — must be set explicitly.
      // onClusterClick overrides the default fitBounds handler which can zoom OUT when the
      // current viewport is already tighter than the cluster bounds. The custom handler calls
      // fitBounds first, then overrides with zoomBefore + 2 if the result is a zoom-out.
      this.#clusterer = new markerClusterer.MarkerClusterer({
        markers: initialMarkers,
        map: this.#map,
        algorithm: new markerClusterer.SuperClusterAlgorithm({ maxZoom: 10 }),
        onClusterClick: (event, cluster, map) => {
          const zoomBefore = map.getZoom();
          map.fitBounds(cluster.bounds);
          // Guard against both zoom-out and over-zoom from fitBounds:
          // - Zoom-out: cluster bounds wider than current view → incremental zoom-in instead
          // - Over-zoom: tightly grouped clusters → cap at 15 (streets/buildings visible)
          // Manual zoom is unrestricted (no maxZoom on Map constructor).
          google.maps.event.addListenerOnce(map, 'idle', () => {
            const zoomAfter = map.getZoom();
            if (zoomAfter < zoomBefore) {
              map.setZoom(Math.min(zoomBefore + 2, 10));
            } else if (zoomAfter > 15) {
              map.setZoom(15);
            }
          });
        },
      });
    } catch (error) {
      // Map is non-critical — the list continues to work without it.
      console.error('[Store Map]', error instanceof Error ? error.message : error);
    }
  }

  /**
   * Handles a `stores:filter` event by updating the clusterer to show only the
   * filtered markers and refitting the map viewport to their bounding box.
   *
   * Uses `clearMarkers()` + `addMarkers()` on the MarkerClusterer — NOT `marker.map = null`.
   * Setting `marker.map = null` triggers MarkerClusterer bug #961 where null'd markers
   * reappear on zoom/scroll events. The clear+add pattern is the correct approach.
   *
   * @param {object[]} mapStores - Filtered stores with valid coordinates.
   */
  #handleFilter(mapStores) {
    if (!this.#clusterer || !this.#map) {
      this.#pendingFilter = mapStores;
      return;
    }

    // Look up AdvancedMarkerElement for each filtered store using the Map reference
    const filteredMarkers = mapStores
      .map((store) => this.#markerMap.get(store))
      .filter(Boolean);

    // CRITICAL: clearMarkers + addMarkers — NOT marker.map = null
    // (MarkerClusterer bug #961: null'd markers reappear on zoom/scroll)
    this.#clusterer.clearMarkers();
    this.#clusterer.addMarkers(filteredMarkers);

    // Refit viewport to the filtered stores' bounding box
    if (filteredMarkers.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      mapStores.forEach((s) => bounds.extend({ lat: s.lat, lng: s.lng }));
      this.#map.fitBounds(bounds, 50);
    }
  }

  /**
   * Handles map:pan-to events from store-search.js.
   * Supports two detail shapes:
   * - { viewport: google.maps.LatLngBounds } — fit to area (city/region)
   * - { lat: number, lng: number, zoom: number } — center + zoom (point/geolocation)
   *
   * @param {{ viewport?: google.maps.LatLngBounds, lat?: number, lng?: number, zoom?: number }} detail
   */
  #handlePanTo(detail) {
    if (!this.#map) return;

    if (detail.viewport) {
      // Use fitBounds + idle listener to get the correct target zoom.
      // fitBounds is async internally — getZoom() returns stale values
      // if read synchronously after fitBounds.
      const targetCenter = detail.viewport.getCenter();
      this.#map.fitBounds(detail.viewport);
      google.maps.event.addListenerOnce(this.#map, 'idle', () => {
        const targetZoom = this.#map.getZoom();
        this.#animateCamera(targetCenter, targetZoom);
      });
    } else if (detail.lat !== undefined && detail.lng !== undefined) {
      this.#animateCamera(
        { lat: detail.lat, lng: detail.lng },
        detail.zoom ?? this.#map.getZoom()
      );
    }
  }

  /**
   * Smoothly animates the map camera to a target center and zoom using
   * fractional zoom via `moveCamera` in a `requestAnimationFrame` loop.
   * Cancels any in-progress animation before starting a new one.
   *
   * @param {google.maps.LatLng | { lat: number, lng: number }} targetCenter
   * @param {number} targetZoom
   * @param {number} [duration=800] - Animation duration in ms.
   */
  #animateCamera(targetCenter, targetZoom, duration = 800) {
    if (this.#animationFrameId) {
      cancelAnimationFrame(this.#animationFrameId);
      this.#animationFrameId = null;
    }

    const startCenter = this.#map.getCenter();
    const startZoom = this.#map.getZoom();
    const startLat = startCenter.lat();
    const startLng = startCenter.lng();
    const endLat = typeof targetCenter.lat === 'function' ? targetCenter.lat() : targetCenter.lat;
    const endLng = typeof targetCenter.lng === 'function' ? targetCenter.lng() : targetCenter.lng;
    const startTime = performance.now();

    const step = () => {
      if (!this.#map) return;

      const t = Math.min((performance.now() - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3); // ease-out cubic

      this.#map.moveCamera({
        center: {
          lat: startLat + (endLat - startLat) * ease,
          lng: startLng + (endLng - startLng) * ease,
        },
        zoom: startZoom + (targetZoom - startZoom) * ease,
      });

      if (t < 1) {
        this.#animationFrameId = requestAnimationFrame(step);
      } else {
        this.#animationFrameId = null;
      }
    };

    this.#animationFrameId = requestAnimationFrame(step);
  }

  /**
   * Handles a `store:selected` event by panning the map to the store's position
   * and opening its InfoWindow.
   *
   * `this.#markerMap.get(store)` is O(1) identity lookup — store object references are
   * shared between mapStores and listStores (RESEARCH Pitfall 3).
   *
   * `#openInfoWindow` calls `this.#infoWindow.close()` first — single-popup enforcement
   * is already baked in (RESEARCH Don't Hand-Roll).
   *
   * `!store.hasValidCoords` guard handles no-coords card clicks silently — no marker
   * exists for these stores.
   *
   * @param {object} store - The selected store from event.detail.store.
   */
  #handleStoreSelected(store) {
    if (!this.#map || !store.hasValidCoords) return;
    const marker = this.#markerMap.get(store);
    if (!marker) return;
    this.#map.panTo({ lat: store.lat, lng: store.lng });
    this.#openInfoWindow(marker, store);
  }

  /**
   * Returns the Google Maps color scheme to use for the map.
   *
   * MAPB-01: Always returns `ColorScheme.LIGHT` regardless of the active Tinker color scheme
   * or the user's system dark mode preference.
   *
   * Phase 3 auto-detected light/dark from CSS variable `--color-background`. That approach
   * broke when Tinker dark color schemes or system dark mode caused the map to render in dark
   * mode while the store locator UI was hardcoded to light mode (Phase 7). Forcing light mode
   * here makes the map consistent with the surrounding UI.
   *
   * Note: `colorScheme` is an init-only Map constructor option — it cannot be changed after
   * the map is created (RESEARCH Anti-Patterns).
   *
   * @param {{ DARK: string, LIGHT: string }} ColorScheme - The ColorScheme enum from `google.maps.importLibrary('core')`.
   * @returns {string} Always `ColorScheme.LIGHT`
   */
  #detectColorScheme(ColorScheme) {
    // MAPB-01: Always light mode regardless of Tinker color scheme or system preference.
    // Phase 3 detected color via CSS variable; Phase 7 hardcoded the UI to light,
    // so the map must match unconditionally.
    return ColorScheme.LIGHT;
  }

  /**
   * Closes the existing InfoWindow, sets new content, and opens it anchored at the clicked marker.
   *
   * Calling `close()` first is required to reset state when the same window is reopened at a
   * different marker. Using `{ anchor, map }` object form prevents the InfoWindow rendering at
   * coordinates (0,0) (RESEARCH Pitfall 3 — old `open(map)` form ignores anchor positioning).
   *
   * @param {google.maps.marker.AdvancedMarkerElement} marker - The clicked marker.
   * @param {MapStore} store - The store data associated with the marker.
   */
  #openInfoWindow(marker, store) {
    this.#infoWindow.close();
    this.#infoWindow.setContent(this.#buildInfoContent(store));
    this.#infoWindow.open({ anchor: marker, map: this.#map });
  }

  /**
   * Builds the HTML string content for an InfoWindow popup.
   *
   * Resolves CSS color variables via `getComputedStyle` at render time rather than using
   * `var(--color-*)` in the HTML string — Google Maps mounts InfoWindow content inside its own
   * DOM subtree which may not inherit CSS variables from the section element (RESEARCH Pitfall 4,
   * Strategy 1).
   *
   * Security note: Store data comes from Shopify metaobjects (admin-authored content, not
   * user-supplied input). HTML escaping is not required in this context.
   *
   * @param {MapStore} store - The store data to display.
   * @returns {string} HTML string for the InfoWindow content.
   */
  #buildInfoContent(store) {
    // Resolve CSS variables at render time — resolved values (e.g. "rgb(30, 30, 30)") are used
    // as literal color values in inline styles, bypassing any CSS variable inheritance issues
    // inside the Google Maps InfoWindow DOM subtree.
    const style = getComputedStyle(this);
    const fg = style.getPropertyValue('--color-foreground').trim();
    const bg = style.getPropertyValue('--color-background').trim();

    const phoneBlock = store.phone
      ? `<p style="margin: 0 0 2px; font-size: 0.8125rem;"><a href="tel:${store.phone}" style="color: inherit; font-size: 0.8125rem;">${store.phone}</a></p>`
      : '';

    const emailBlock = store.email
      ? `<p style="margin: 0 0 2px; font-size: 0.8125rem;"><a href="mailto:${store.email}" style="color: inherit; font-size: 0.8125rem;">${store.email}</a></p>`
      : '';

    // Website link is only rendered when store.url is populated (MAP-04 requirement).
    const websiteBlock = store.url
      ? `<p style="margin: 0; font-size: 0.8125rem;"><a href="${store.url}" target="_blank" rel="noopener" style="color: inherit; font-size: 0.8125rem;">Visit website</a></p>`
      : '';

    return `<div style="color: ${fg}; background: ${bg}; padding: 4px 8px; font-family: inherit; max-width: 250px;">
  <strong style="font-size: 0.875rem; display: block; margin-bottom: 2px;">${store.name}</strong>
  <p style="margin: 0 0 2px; font-size: 0.8125rem;">${store.street}, ${store.postal_code} ${store.city}</p>
  <p style="margin: 0 0 2px; font-size: 0.8125rem;">${store.country}</p>
  ${phoneBlock}
  ${emailBlock}
  ${websiteBlock}
</div>`;
  }
}

if (!customElements.get('store-map')) {
  customElements.define('store-map', StoreMap);
}
